import classNames from "classnames";
import React from "react";
import { SketchPicker } from "react-color";
import "./kuika-color-picker.scss";

export interface IKuikaColorPickerProps {
  color: string;
  onChange: (color: string) => void;
  position?: "top" | "bottom";
  /**
   * Overrides the swatch button's id. The default names the control's role rather than the
   * value it edits, so a page with more than one picker cannot address them apart.
   */
  dataTestId?: string;
}

export interface IKuikaColorPickerState {
  isOpen: boolean;
  /** Resolved on open when the caller did not pin a `position`. */
  dropUp: boolean;
}

/** `SketchPicker`'s own height, near enough to decide which way it fits. */
const PICKER_HEIGHT = 320;

class KuikaColorPicker extends React.PureComponent<IKuikaColorPickerProps, IKuikaColorPickerState> {
  kuikaColorPickerRef = React.createRef<HTMLDivElement>();

  static presetColors: string[] = [
    "transparent",
    "#FFFFFF",
    "#D9D9D9",
    "#8C8C8C",
    "#000000",
    "#F5222D",
    "#FA541C",
    "#FA8C16",
    "#FAAD14",
    "#FADB14",
    "#A0D911",
    "#52C41A",
    "#13C2C2",
    "#1890FF",
    "#722ED1",
    "#EB2F96"
  ];

  constructor(props: IKuikaColorPickerProps) {
    super(props);
    this.state = {
      isOpen: false,
      dropUp: false
    };
  }

  componentDidMount() {
    this.addOnClickOutsideListener();
  }

  componentWillUnmount() {
    this.removeOnClickOutsideListener();
  }

  getPickerClassName = () => {
    const dropUp: boolean = this.props.position ? this.props.position === "top" : this.state.dropUp;
    return classNames({
      "kuika-color-picker--picker": true,
      "kuika-color-picker--picker-open": this.state.isOpen,
      "kuika-color-picker--picker-top": dropUp,
      "kuika-color-picker--picker-bottom": !dropUp
    });
  };

  /**
   * Which way the panel opens when the caller did not say.
   *
   * The panel is absolutely positioned, so a scrolling ancestor clips it rather than
   * scrolling to it — in the Config Manager's 300px right column the splash colour picker
   * opened downwards into the column's own bottom edge and could not be reached at all.
   * The nearest clipping ancestor is what it has to fit inside, not the viewport.
   */
  shouldDropUp = (): boolean => {
    const anchor: HTMLDivElement | null = this.kuikaColorPickerRef.current;
    if (!anchor) return false;

    let bottomLimit: number = window.innerHeight;
    let topLimit = 0;
    let parent: HTMLElement | null = anchor.parentElement;
    while (parent) {
      const overflowY: string = window.getComputedStyle(parent).overflowY;
      if (overflowY !== "visible" && overflowY !== "clip") {
        const clip: DOMRect = parent.getBoundingClientRect();
        bottomLimit = Math.min(bottomLimit, clip.bottom);
        topLimit = Math.max(topLimit, clip.top);
        break;
      }
      parent = parent.parentElement;
    }

    const rect: DOMRect = anchor.getBoundingClientRect();
    const roomBelow: number = bottomLimit - rect.bottom;
    const roomAbove: number = rect.top - topLimit;
    return roomBelow < PICKER_HEIGHT && roomAbove > roomBelow;
  };

  onClickColorPicker = () => {
    const isOpen: boolean = !this.state.isOpen;
    this.setState({ isOpen, dropUp: isOpen ? this.shouldDropUp() : this.state.dropUp });
  };

  addOnClickOutsideListener = () => {
    document.addEventListener("click", this.onClickOutside);
  };

  removeOnClickOutsideListener = () => {
    document.removeEventListener("click", this.onClickOutside);
  };

  onClickOutside = (event: MouseEvent) => {
    if (
      this.state.isOpen &&
      this.kuikaColorPickerRef.current &&
      !this.kuikaColorPickerRef.current.contains(event.target as Node)
    ) {
      this.setState({ isOpen: false });
    }
  };

  render() {
    return (
      <div ref={this.kuikaColorPickerRef} className="kuika-color-picker">
        <button
          // Without this the swatch is a submit button, and every picker that sits inside
          // a form saved that form when it was opened.
          type="button"
          data-testid={this.props.dataTestId ?? "addButtonModalColorPicker"}
          className="kuika-color-picker--button"
          onClick={this.onClickColorPicker}
        >
          <div className="kuika-color-picker--color" style={{ backgroundColor: this.props.color }} />
          <span className="kuika-color-picker--text">{this.props.color ?? ""}</span>
        </button>

        <SketchPicker
          className={this.getPickerClassName()}
          presetColors={KuikaColorPicker.presetColors}
          color={this.props.color}
          onChange={(color) => this.props.onChange(color.hex)}
        />
      </div>
    );
  }
}

export default KuikaColorPicker;
